<template>
  <footer class="mt-4 border-t py-16 lg:py-24 bg-background">
    <div class="container mx-auto px-4">
      <!-- Desktop Footer Links (client-only to avoid SSR/client mismatches) -->
      <ClientOnly>
        <section v-if="footerSections.length > 0" class="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="section in footerSections" :key="section.heading">
            <p class="mb-4 text-muted-foreground lg:text-sm">{{ section.heading }}</p>
            <ul class="flex flex-col gap-3">
              <li v-for="link in section.links" :key="link.url">
                <NuxtLink 
                  v-if="!isExternalLink(link.url)"
                  class="font-semibold hover:text-primary" 
                  :to="link.url"
                >
                  {{ link.title }}
                </NuxtLink>
                <a 
                  v-else
                  class="font-semibold hover:text-primary"
                  :href="link.url"
                  :target="link.target"
                >
                  {{ link.title }}
                </a>
              </li>
            </ul>
          </div>
        </section>
      </ClientOnly>

      <!-- Mobile Footer Links with Accordion (client-only) -->
      <ClientOnly>
        <section v-if="footerSections.length > 0" class="md:hidden space-y-2">
            <Disclosure v-for="section in footerSections" :key="section.heading" v-slot="{ open }" as="div" class="border-b border-border pb-2">
              <DisclosureButton class="flex w-full justify-between items-center py-3">
                <span class="text-lg font-medium text-foreground">{{ section.heading }}</span>
                <ChevronDownIcon 
                  class="h-5 w-5 text-muted-foreground transition-transform duration-200" 
                  :class="{ 'rotate-180': open }"
                  aria-hidden="true" 
                />
              </DisclosureButton>
              <DisclosurePanel class="pb-3 pt-1">
                <ul class="flex flex-col gap-3">
                  <li v-for="link in section.links" :key="link.url" class="pt-4">
                    <NuxtLink 
                      v-if="!isExternalLink(link.url)"
                      class="font-light text-2xl ml-2 hover:text-primary" 
                      :to="link.url"
                    >
                      {{ link.title }}
                    </NuxtLink>
                    <a 
                      v-else
                      class="font-semibold hover:text-primary"
                      :href="link.url"
                      :target="link.target"
                    >
                      {{ link.title }}
                    </a>
                  </li>
                </ul>
              </DisclosurePanel>
            </Disclosure>
          </section>
      </ClientOnly>

      <!-- Identity and address (client-only to ensure logo/address match hydrated store) -->
      <ClientOnly>
        <section class="mt-12 flex flex-col justify-between gap-5 sm:border-t pt-8 lg:mt-16 lg:flex-row">
          <NuxtLink to="/" class="flex items-center gap-3">
            <img 
              :src="siteLogo" 
              :alt="siteName"
              class="h-8 w-auto"
            />
            <span class="text-lg font-semibold">{{ siteName }}</span>
          </NuxtLink>
          <div class="flex flex-col gap-2 text-sm text-muted-foreground">
            <p v-if="siteAddress">{{ siteAddress }}</p>
            <p v-if="siteLmct">LMCT {{ siteLmct }}</p>
            <p>&copy; {{ currentYear }} {{ siteName }}. All rights reserved.</p>
          </div>
        </section>
      </ClientOnly>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useSiteStore } from '~/stores/site'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'

const siteStore = useSiteStore()

// Add a reactive flag to track store readiness
const isStoreReady = computed(() => {
  // Consider store ready if it has basic data or if we're on server
  return process.server || siteStore.isInitialized || !!siteStore.name
})

// Define prop for the hydrate-on-visible attribute
const props = defineProps({
  'hydrate-on-visible': {
    type: Boolean,
    default: false
  }
})

// Computed properties to prevent hydration mismatches
const footerSections = computed(() => {
  // Only show footer sections when store is ready to prevent hydration mismatches
  if (!isStoreReady.value || !siteStore.sitelinks?.footer) {
    return []
  }
  return siteStore.sitelinks.footer
})

const siteName = computed(() => {
  return isStoreReady.value ? (siteStore.name || 'My Nuxt App') : 'My Nuxt App'
})

const siteLogo = computed(() => {
  // Use fallback when store is not ready to ensure consistency
  return isStoreReady.value ? (siteStore.logo || '/logo.png') : '/logo.png'
})

const siteAddress = computed(() => {
  return isStoreReady.value ? (siteStore.showroom_address || '') : ''
})

const siteLmct = computed(() => {
  return isStoreReady.value ? (siteStore.lmct || '') : ''
})

const currentYear = computed(() => {
  return new Date().getFullYear()
})

// Helper function to check if a URL is external
const isExternalLink = (url: string): boolean => {
  return url.startsWith('http') || url.startsWith('//') || url.startsWith('tel:') || url.startsWith('mailto:')
}
</script>